import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import HttpService from "../../services/HttpService";
import RenderOnRole from "../../Utilities/RenderOnRole";
import "../../css/index.css";
import "../../css/Pages.css";
import AccountCreation from "./Components/AccountCreation";
import AccountActions from "./Components/AccountActions";

const AccountBeheerPagina = () => {
	const { t } = useTranslation();
	const [accounts, setAccounts] = useState([]);
	const [companyNames, setCompanyNames] = useState({});
	const [searchTerm, setSearchTerm] = useState("");
	const [sortKey, setSortKey] = useState("name");
	const [openMenuId, setOpenMenuId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [showCreation, setShowCreation] = useState(false);

	const fetchAccounts = useCallback(async (showLoading = true) => {
		if (showLoading) setLoading(true);
		try {
			HttpService.configure();
			const http = HttpService.getAxiosClient();
			const response = await http.get("/Accounts");
			const loadedAccounts = Array.isArray(response.data) ? response.data : [];
			setAccounts(loadedAccounts);

			const debtorNumbers = [...new Set(loadedAccounts.flatMap((account) => (
				account.attributes?.debinr || []
			).map(String)))];
			if (debtorNumbers.length > 0) {
				const companiesResponse = await http.get(`/Klanten?limited=true&${debtorNumbers.map((debiNr) => `debiNr=${encodeURIComponent(debiNr)}`).join("&")}`);
				const names = companiesResponse.data || [];
				setCompanyNames(Object.fromEntries(debtorNumbers.map((debiNr, index) => [debiNr, names[index] || debiNr])));
			} else {
				setCompanyNames({});
			}
			setError(false);
		} catch (err) {
			setError(true);
		} finally {
			setLoading(false);
		}
	}, []);

	const getAccountCompanies = (account) => (account.attributes?.debinr || [])
		.map(String)
		.map((debiNr) => companyNames[debiNr] || debiNr);

	useEffect(() => {
		fetchAccounts();
	}, [fetchAccounts]);

	const displayedAccounts = useMemo(() => {
		const search = searchTerm.trim().toLowerCase();

		return accounts
			.filter((account) => {
				if (!search) return true;
				const name = `${account.firstName || ""} ${account.lastName || ""}`;
				return name.toLowerCase().includes(search);
			})
			.sort((first, second) => {
				if (sortKey === "email") {
					return (first.email || "").localeCompare(second.email || "");
				}
				if (sortKey === "status") {
					return Number(second.enabled) - Number(first.enabled);
				}
				const firstName = `${first.lastName || ""} ${first.firstName || ""}`;
				const secondName = `${second.lastName || ""} ${second.firstName || ""}`;
				return firstName.localeCompare(secondName);
			});
	}, [accounts, searchTerm, sortKey]);

	const handleAccountCreated = async () => {
		setShowCreation(false);
		await fetchAccounts(false);
	};

	const handleAccountDeleted = (accountId) => {
		setAccounts((current) => current.filter((account) => account.id !== accountId));
		setOpenMenuId(null);
	};

	if (loading) {
		return <div className="loading-container"><div className="loading-text">{t("accounts.loading")}</div></div>;
	}

	if (error) {
		return <div className="error-alert"><h3>{t("common.error")}</h3><p>{t("accounts.error")}</p></div>;
	}

	return (
		<RenderOnRole roles={["Account_Beheerder", "Administrator", "SD_Medewerker"]}>
			<div className="page-wrapper account-management-page">
				<div className="page-header account-page-header">
					<div>
						<h2>{t("accounts.title")}</h2>
						<p>{t("accounts.description")}</p>
					</div>
					<button className="btn btn-primary" type="button" onClick={() => setShowCreation(true)}>
						{t("accounts.addUser")}
					</button>
				</div>

				<div className="page-content">
				{showCreation && (
					<div className="parameter-section">
						<h3>{t("accounts.createTitle")}</h3>
						<AccountCreation onCreated={handleAccountCreated} onCancel={() => setShowCreation(false)} />
					</div>
				)}
					<div className="filter-bar">
						<div className="filter-grid">
							<div className="filter-group">
								<label className="filter-label" htmlFor="account-search">{t("accounts.name")}</label>
								<input
									id="account-search"
									className="filter-input"
									type="search"
									placeholder={t("accounts.searchName")}
									value={searchTerm}
									onChange={(event) => setSearchTerm(event.target.value)}
								/>
							</div>
							<div className="filter-group">
								<label className="filter-label" htmlFor="account-sort">{t("accounts.sortBy")}</label>
								<select id="account-sort" className="filter-select" value={sortKey} onChange={(event) => setSortKey(event.target.value)}>
									<option value="name">{t("accounts.name")}</option>
									<option value="email">{t("accounts.username")}</option>
									<option value="status">{t("accounts.status")}</option>
								</select>
							</div>
						</div>
					</div>

					<div className="table-wrapper table-scroll">
						<table className="table" name="accountsTable">
							<thead>
								<tr>
									<th>{t("accounts.name")}</th>
									<th>{t("accounts.username")}</th>
									<th>{t("accounts.companies")}</th>
									<th>{t("accounts.roles")}</th>
									<th>{t("accounts.status")}</th>
									<th aria-label={t("accounts.actions")} />
								</tr>
							</thead>
							<tbody>
								{displayedAccounts.length > 0 ? displayedAccounts.map((account) => (
									<tr key={account.id}>
										<td>{account.firstName} {account.lastName}</td>
										<td>{account.email}</td>
										<td>{getAccountCompanies(account).join(", ") || t("accounts.noCompanies")}</td>
										<td>{(account.roles || []).join(", ")}</td>
										<td>{account.enabled ? t("accounts.enabled") : t("accounts.disabled")}</td>
										<td className="account-actions-cell">
											<button className="btn btn-secondary" type="button" onClick={() => setOpenMenuId(openMenuId === account.id ? null : account.id)} aria-expanded={openMenuId === account.id}>
												{t("accounts.actions")}
											</button>
											{openMenuId === account.id && (
														<div className="account-actions-menu" role="menu">
															<AccountActions account={account} onChanged={() => fetchAccounts(false)} onDeleted={() => handleAccountDeleted(account.id)} />
														</div>
											)}
										</td>
									</tr>
								)) : (
									<tr className="empty-row"><td colSpan="6">{t("accounts.noAccounts")}</td></tr>
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</RenderOnRole>
	);
};

export default AccountBeheerPagina;
